import { Component, EventEmitter, Output, OnInit, OnChanges, SimpleChanges, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { WizardStepBasicComponent } from './steps/wizard-step-basic/wizard-step-basic.component';
import { WizardStepBlockchainComponent } from './steps/wizard-step-blockchain/wizard-step-blockchain.component';
import { WizardStepDocsComponent } from './steps/wizard-step-docs/wizard-step-docs.component';

@Component({
  selector: 'app-project-wizard',
  templateUrl: './project-wizard.component.html',
  styleUrls: ['./project-wizard.component.css'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, WizardStepBasicComponent, WizardStepBlockchainComponent, WizardStepDocsComponent]
})
export class ProjectWizardComponent implements OnInit, OnChanges {
  @Input() initialData: any = null;
  @Output() onComplete = new EventEmitter<any>();
  @Output() onCancel = new EventEmitter<void>();

  wizardForm!: FormGroup;
  currentStep: number = 1;

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.initForm();
    if (this.initialData) {
      this.populateForm(this.initialData);
      this.setupReadonlyFields();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialData'] && !changes['initialData'].firstChange) {
      if (this.wizardForm) {
        this.populateForm(changes['initialData'].currentValue);
      }
    }
  }

  private initForm(): void {
    this.wizardForm = this.fb.group({
      basicInfo: this.fb.group({
        title: ['', [Validators.required, Validators.maxLength(180)]],
        amountToSell: [null, [Validators.required, Validators.min(1)]],
        minimumInvestment: [null, [Validators.required, Validators.min(1)]],
        activationDate: [null, Validators.required],
        proyectStatus: ['En estudio', Validators.required],
        propertyType: ['House Flipping', Validators.required]
      }),
      blockchain: this.fb.group({
        tokenName: ['', Validators.required],
        tokenSymbol: ['', Validators.required],
        tokenPrice: [null, [Validators.required, Validators.min(0.01)]],
        beneficiaryAddress: [''],
        stableCoinAddress: ['0x8f3Cf7ad23Cd3CaDbD9735AFf958023239c6A063'],
        aggregatorStableCoinDollar: ['0x4746DeC9e833A82EC7C2C1356372CcF2cfcD2F3D']
      }),
      docs: this.fb.group({
        whitepaperFile: [null, Validators.required],
        dossierFile: [null, Validators.required],
        loanSubscriptionAgreementFile: [null, Validators.required],
        propertyRegistryFile: [null, Validators.required],
        agentMandateContractFile: [null]
      })
    });
  }

  private populateForm(data: any): void {
    console.log('🐞 [DEBUG WIZARD] data.activationDate:', data.activationDate);
    // Formatear date para input type="datetime-local"
    let formattedDate = null;
    try {
      let date = new Date();
      if (data.activationDate) {
        if (data.activationDate.toDate) {
            date = data.activationDate.toDate();
        } else if (data.activationDate instanceof Date) {
            date = data.activationDate;
        } else {
            date = new Date(data.activationDate);
        }
      }
        
      if (!isNaN(date.getTime())) {
          // Actually, to display LOCAL time instead of UTC, we should do:
          const tzoffset = (new Date()).getTimezoneOffset() * 60000; // offset in milliseconds
          const localISOTime = (new Date(date.getTime() - tzoffset)).toISOString().slice(0, 16);
          formattedDate = localISOTime;
      }
    } catch (e) {
      console.error('Error parseando activationDate (wizard):', e);
    }
    console.log('🐞 [DEBUG WIZARD] formattedDate local:', formattedDate);

    // Basic Info
    this.wizardForm.get('basicInfo')?.patchValue({
      title: data.title || '',
      minimumInvestment: data.minimumInvestment || null,
      amountToSell: data.amountToSell || null,
      propertyType: data.propertyType || 'House Flipping',
      activationDate: formattedDate || null,
      proyectStatus: data.proyectStatus || 'En estudio'
    });

    // Blockchain
    this.wizardForm.get('blockchain')?.patchValue({
      tokenName: data.tokenName || '',
      tokenSymbol: data.tokenSymbol || '',
      tokenPrice: data.tokenPrice || null,
      beneficiaryAddress: data.beneficiaryAddress || '',
      stableCoinAddress: data.stableCoinAddress || '0x8f3Cf7ad23Cd3CaDbD9735AFf958023239c6A063',
      aggregatorStableCoinDollar: data.aggregatorStableCoinDollar || '0x4746DeC9e833A82EC7C2C1356372CcF2cfcD2F3D'
    });

    // Docs
    const docsGroup = this.wizardForm.get('docs') as FormGroup;
    const firstProp = data.properties && data.properties.length > 0 ? data.properties[0] : {};
    const docs = data.additionalDocuments || {};
    
    // Busca las URLs en varios niveles para ser retrocompatible con proyectos antiguos
    const loanUrl = data.loanSubscriptionAgreement || docs.loanSubscriptionAgreement || firstProp?.loanSubscriptionAgreement || firstProp?.additionalDocuments?.loanSubscriptionAgreement || data.urlInfoTokenPdf || '';
    const registryUrl = data.propertyRegistry || docs.propertyRegistry || firstProp?.propertyRegistry || firstProp?.additionalDocuments?.propertyRegistry || '';
    const mandateUrl = data.agentMandateContract || docs.agentMandateContract || firstProp?.agentMandateContract || firstProp?.additionalDocuments?.agentMandateContract || '';
    
    docsGroup.patchValue({
      whitepaperFile: data.urlWhitepapperPdf ? { isExisting: true, name: this.getFilenameFromUrl(data.urlWhitepapperPdf, 'Whitepaper') } : null,
      dossierFile: data.urlDosierPdf ? { isExisting: true, name: this.getFilenameFromUrl(data.urlDosierPdf, 'Dossier') } : null,
      loanSubscriptionAgreementFile: loanUrl ? { isExisting: true, name: this.getFilenameFromUrl(loanUrl, 'Acuerdo') } : null,
      propertyRegistryFile: registryUrl ? { isExisting: true, name: this.getFilenameFromUrl(registryUrl, 'Nota Simple') } : null,
      agentMandateContractFile: mandateUrl ? { isExisting: true, name: this.getFilenameFromUrl(mandateUrl, 'Contrato Mandato') } : null
    });

    if (data.urlWhitepapperPdf) docsGroup.get('whitepaperFile')?.clearValidators();
    if (data.urlDosierPdf) docsGroup.get('dossierFile')?.clearValidators();
    if (loanUrl) docsGroup.get('loanSubscriptionAgreementFile')?.clearValidators();
    if (registryUrl) docsGroup.get('propertyRegistryFile')?.clearValidators();

    docsGroup.get('whitepaperFile')?.updateValueAndValidity();
    docsGroup.get('dossierFile')?.updateValueAndValidity();
    docsGroup.get('loanSubscriptionAgreementFile')?.updateValueAndValidity();
    docsGroup.get('propertyRegistryFile')?.updateValueAndValidity();
    docsGroup.get('agentMandateContractFile')?.updateValueAndValidity();

    // Bloquear campos de blockchain si el proyecto ya está desplegado
    const isDeployed = data.blockchain === true || !!data.tokensAddress || !!data.contractAddress;
    if (isDeployed) {
      const basicInfoGroup = this.wizardForm.get('basicInfo') as FormGroup;
      basicInfoGroup.get('minimumInvestment')?.disable();
      basicInfoGroup.get('amountToSell')?.disable();
      
      const blockchainGroup = this.wizardForm.get('blockchain') as FormGroup;
      blockchainGroup.get('tokenName')?.disable();
      blockchainGroup.get('tokenSymbol')?.disable();
      blockchainGroup.get('tokenPrice')?.disable();
      blockchainGroup.get('beneficiaryAddress')?.disable();
      blockchainGroup.get('stableCoinAddress')?.disable();
      blockchainGroup.get('aggregatorStableCoinDollar')?.disable();
    }
  }

  private setupReadonlyFields(): void {
    if (!this.initialData || !this.initialData.tokensAddress) {
      return;
    }
    
    // Si el proyecto ya está desplegado en la blockchain, desactivar campos inmutables
    const basicGroup = this.getFormGroup('basicInfo');
    basicGroup.get('amountToSell')?.disable();
    basicGroup.get('minimumInvestment')?.disable();

    const blockchainGroup = this.getFormGroup('blockchain');
    blockchainGroup.get('tokenName')?.disable();
    blockchainGroup.get('tokenSymbol')?.disable();
    blockchainGroup.get('tokenPrice')?.disable();
    blockchainGroup.get('beneficiaryAddress')?.disable();
    blockchainGroup.get('stableCoinAddress')?.disable();
    blockchainGroup.get('aggregatorStableCoinDollar')?.disable();
  }

  getFormGroup(groupName: string): FormGroup {
    return this.wizardForm.get(groupName) as FormGroup;
  }

  goToNextStep(): void {
    if (this.currentStep < 3) {
      this.currentStep++;
    }
  }

  goToPreviousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  submitWizard(): void {
    if (this.wizardForm.valid) {
      // Flatten the data to match what new-proyect expects
      const rawData = this.wizardForm.getRawValue();
      const flatData = {
        ...rawData.basicInfo,
        ...rawData.blockchain,
        ...rawData.docs
      };
      this.onComplete.emit(flatData);
    }
  }

  // Extrae el nombre original del archivo desde una URL de Firebase Storage
  private getFilenameFromUrl(url: string, defaultName: string): string {
    if (!url || typeof url !== 'string') return `${defaultName} (Ya subido)`;
    try {
      // Extraer la parte del path ignorando parámetros query
      const urlObj = new URL(url);
      const pathname = decodeURIComponent(urlObj.pathname);
      
      // Firebase almacena las carpetas/archivos separados por '%2F', que decodeURIComponent convierte a /
      const parts = pathname.split('/');
      const lastPart = parts[parts.length - 1];
      
      // El último segmento suele ser el nombre del archivo
      if (lastPart) {
        return `${lastPart} (Ya subido)`;
      }
    } catch (e) {
      // Si falla el parseo (por ej. si no es una URL completa), fallback
    }
    return `${defaultName} (Ya subido)`;
  }
}
